// Smooth wheel zoom for three's OrbitControls.
//
// OrbitControls applies a wheel event's whole zoom in the frame it arrives
// (a mouse notch is a 6% jump at zoomSpeed 1.2), so a wheel reads as steps.
// This holds each event back and feeds it to the controls a share a frame,
// as smaller wheel events of its own, so a notch eases in over about a
// quarter of a second and fast scrolling glides. The controls still do all
// the zooming (their limits, zoomSpeed, anything wrapped round their update
// such as the viewer's Sun zoom floor), and the total zoom per notch is
// what it was: OrbitControls' step is 0.95^(zoomSpeed x deltaY / 100), so
// pieces of a delta multiply back to the whole.
//
// Only events OrbitControls would act on are held: with the controls or
// their zoom off (fly mode, the deep-space photo zoom, the screensaver) the
// wheel goes through untouched to whoever owns it then.

// Time constant of the ease, seconds: 63% of a notch lands in TAU, 95% in
// three.
const TAU = 0.08;

export function smoothWheelZoom(controls) {
  const el = controls.domElement;
  let pending = 0;        // pixels of deltaY not yet given to the controls
  let clientX = 0;
  let clientY = 0;
  let replaying = false;
  let ctrlHeld = false;
  let raf = 0;
  let last = 0;

  // OrbitControls multiplies a ctrl+wheel (a trackpad pinch) by 10 unless
  // the Control key itself is down; the replayed events carry no ctrlKey,
  // so the same factor is applied here once.
  window.addEventListener('keydown', (e) => { if (e.key === 'Control') ctrlHeld = true; });
  window.addEventListener('keyup', (e) => { if (e.key === 'Control') ctrlHeld = false; });
  window.addEventListener('blur', () => { ctrlHeld = false; });

  function frame(now) {
    raf = 0;
    if (!controls.enabled || !controls.enableZoom) {
      pending = 0;
      return;
    }
    const dt = Math.min(Math.max((now - last) / 1000, 0), 0.1);
    last = now;
    let step = pending * (1 - Math.exp(-dt / TAU));
    if (Math.abs(pending - step) < 0.5) step = pending;
    pending -= step;
    if (step !== 0) {
      replaying = true;
      try {
        el.dispatchEvent(new WheelEvent('wheel', {
          deltaY: step, deltaMode: 0, clientX, clientY, bubbles: true, cancelable: true,
        }));
      } finally {
        replaying = false;
      }
    }
    if (pending !== 0) raf = requestAnimationFrame(frame);
  }

  // Window, capture phase: ahead of OrbitControls' own listener on the
  // canvas in every browser.
  window.addEventListener('wheel', (e) => {
    if (replaying || e.target !== el) return;
    if (!controls.enabled || !controls.enableZoom) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    let dy = e.deltaY;
    if (e.deltaMode === 1) dy *= 16;
    else if (e.deltaMode === 2) dy *= 100;
    if (e.ctrlKey && !ctrlHeld) dy *= 10;
    // A reversal drops what is still to come of the other way.
    if (pending !== 0 && Math.sign(dy) !== Math.sign(pending)) pending = 0;
    pending += dy;
    clientX = e.clientX;
    clientY = e.clientY;
    if (!raf) {
      last = performance.now();
      raf = requestAnimationFrame(frame);
    }
  }, { capture: true, passive: false });

  return {
    // Drops any zoom still easing in (a focus move taking the camera).
    cancel() { pending = 0; },
  };
}
